import { useEffect, useState, useCallback } from "react";
import { getInsights, type InsightsData } from "@/services/insightsApi";
import { ApiError } from "@/services/api";
import { type User } from "firebase/auth";

/**
 * Hook for fetching and managing insights data.
 * Handles loading states, error handling, and token management.
 *
 * @param canvasId - The canvas ID to fetch insights for
 * @param isProtected - Whether the canvas requires authentication
 * @param user - The authenticated user (if protected)
 * @param canvasLoading - Whether the canvas data is still loading (prevents premature fetching)
 * @returns Object with insights data, loading state, error state, error status, and refresh function
 */
export function useInsights(
  externalId: string | undefined,
  _isProtected: boolean,
  user: User | null | undefined,
  canvasLoading: boolean | undefined,
) {
  const [insights, setInsights] = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const loadInsights = useCallback(async () => {
    // Wait for canvas to load first
    if (canvasLoading || !externalId) {
      setLoading(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorStatus(null);
      let token: string | undefined = undefined;
      if (user) {
        token = await user.getIdToken();
      }
      const data = await getInsights({
        externalId,
        token,
      });
      setInsights(data);
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      if (err instanceof ApiError) {
        setError(err.message);
        setErrorStatus(err.status);
      } else {
        setError((err as Error).message);
        setErrorStatus(null);
      }
    } finally {
      setLoading(false);
    }
  }, [canvasLoading, externalId, user]);

  useEffect(() => {
    const controller = new AbortController();
    loadInsights();
    return () => {
      controller.abort();
    };
  }, [loadInsights]);

  const refresh = useCallback(() => {
    loadInsights();
  }, [loadInsights]);

  return {
    insights,
    loading,
    error,
    errorStatus,
    refresh,
  };
}
